import React, { useState } from "react";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * PitchPolish — small "✨ AI polish" button rendered under a text field.
 * On click, sends the current text to Claude and shows a suggested rewrite
 * with a clarity score and reasons. Founder can Accept (calls onAccept with
 * the rewrite string) or Dismiss.
 *
 * Props:
 *   field   — one of one_line_pitch | problem_statement | solution | traction
 *   text    — the current field value (string)
 *   onAccept(rewrite) — called when founder taps Accept
 */
export default function PitchPolish({ field, text, onAccept }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const t = (text || "").trim();
    if (!t) {
      toast.info("Type a first draft first, then click AI polish.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/startup/ai/pitch-polish", { field, text: t });
      setResult(res.data);
    } catch (e) { toast.error("Couldn't polish that."); }
    finally { setBusy(false); }
  };

  const accept = () => {
    if (!result?.rewrite) return;
    onAccept?.(result.rewrite);
    toast.success("Rewrite applied. Don't forget to review.");
    setResult(null);
  };

  const dismiss = () => setResult(null);

  const scoreTone = result?.score == null ? "muted"
    : result.score >= 80 ? "emerald"
    : result.score >= 60 ? "amber"
    : "rose";
  const toneClass = {
    emerald: "text-emerald-700 border-emerald-300 bg-emerald-50",
    amber:   "text-amber-700 border-amber-300 bg-amber-50",
    rose:    "text-rose-700 border-rose-300 bg-rose-50",
    muted:   "text-ink-muted border-navy/15 bg-navy/[0.02]",
  }[scoreTone];

  return (
    <div className="mt-1" data-testid={`pitch-polish-${field}`}>
      {!result && (
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 hover:text-emerald-800 font-mono uppercase tracking-widest disabled:opacity-50"
          data-testid={`pitch-polish-btn-${field}`}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          {busy ? "Polishing…" : "AI polish"}
        </button>
      )}

      {result && (
        <div
          className={`mt-2 rounded-lg border ${toneClass} p-3`}
          data-testid={`pitch-polish-result-${field}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`text-[10px] font-mono uppercase tracking-widest`}>Clarity score</div>
              <div className="font-heading text-lg" data-testid={`pitch-polish-score-${field}`}>
                {result.score}
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-ink-muted hover:text-ink"
              aria-label="Dismiss suggestion"
              data-testid={`pitch-polish-dismiss-${field}`}
            ><X size={14} /></button>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
            Suggested rewrite
          </div>
          <div
            className="text-sm text-ink bg-white rounded p-2 border border-navy/10 leading-relaxed mb-2 whitespace-pre-wrap"
            data-testid={`pitch-polish-rewrite-${field}`}
          >
            {result.rewrite}
          </div>

          {result.reasons && result.reasons.length > 0 && (
            <ul className="text-[11px] text-ink-soft space-y-0.5 mb-3">
              {result.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="text-emerald-600 mt-0.5">•</span>{r}
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={accept}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading inline-flex items-center gap-1"
              data-testid={`pitch-polish-accept-${field}`}
            >
              <Check size={12} /> Use this
            </button>
            <button
              onClick={dismiss}
              className="text-[11px] text-ink-muted hover:text-ink"
              data-testid={`pitch-polish-keep-${field}`}
            >
              Keep mine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
